import * as Scrivito from "scrivito";
import I18n from "^/config/i18n";
import setLanguage from "^/utils/setLanguage";

import { jsonLd } from "^/Widgets/SchemaWidget/Schemas";
import { SchemaWidgetComponent } from "^/Widgets/SchemaWidget/SchemaWidgetComponent";
import HidePageIfInactive from "../../Components/HidePageIfInactive";
import ContactImage from "./ContactImage";
import { InpageNavigationWidget } from "../../Widgets/InpageNavigationWidget/InpageNavigationWidgetComponent";

function ContactPage({ page }) {
  if (!page) {
    return null;
  }

  setLanguage(page);

  const getPostalAddress = (street, postalCode, officeLocation) =>
    !street && !postalCode && !officeLocation
      ? ""
      : `${street}, ${postalCode} ${
          officeLocation === "Heilbronn II" ? "Heilbronn" : officeLocation
        } `;

  const getOfficeAddress = (room, campus) =>
    !room && !campus ? "" : `${room} ${campus}`;

  const room = page.get("room");
  const campus = page.get("campus");
  const officeAdress =
    page.get("altOfficeAddress") || getOfficeAddress(room, campus);

  const street = page.get("street");
  const postalCode = page.get("postalCode");
  const officeLocation = page.get("officeLocation");
  const postalAddress =
    page.get("altPostalAddress") ||
    getPostalAddress(street, postalCode, officeLocation);

  const phone = page.get("altPhoneNumber") || page.get("phoneNumber");

  const contactItems = [
    {
      text: "Telefon",
      itemProp: "telephone",
      content: phone,
      href: `tel:${phone}`,
    },
    {
      text: "Email",
      itemProp: "email",
      content: page.get("email"),
      href: `mailto:${page.get("email")}`,
    },
    {
      text: "AlternativeEmail",
      itemProp: "altEmail",
      content: page.get("altEmail"),
      href: `mailto:${page.get("altEmail")}`,
    },
    {
      text: "Fax",
      content: page.get("faxNumber"),
    },
    {
      text: "Officeadress",
      content: officeAdress,
    },
    {
      text: "Postadresse",
      itemProp: "address",
      content: postalAddress,
    },
    {
      text: "Sprechzeiten",
      content: page.get("consultation"),
    },
    {
      text: "Pronomen",
      content: page.get("pronoun"),
    },
  ];

  const hasMedia = page.get("image");
  const mediaClass = hasMedia ? "col-12 col-lg-5" : "";
  const contentClass = hasMedia ? "col-12 col-lg-7" : "col-12";
  const hasContent =
    Scrivito.isInPlaceEditingActive() || page.get("content")?.length > 0;
  const belongings = page.get("belongings") || [];
  const children = page.children();

  const json = jsonLd.Person(page, page);

  return (
    <>
      <div className="page-header-widget bg-blue --contact">
        <div className="container">
          <div className="row">
            {hasMedia && (
              <div className={`${mediaClass} position-relative`}>
                <div className="page-header-widget__media">
                  <ContactImage page={page} />
                </div>
              </div>
            )}
            <div className={contentClass}>
              <h1 className="page-header-widget__headline m-0">
                {`${page.get("salutation") || ""} ${page.get(
                  "firstName"
                )} ${page.get("lastName")}`}
              </h1>
              <Scrivito.ContentTag
                tag="h3"
                className="page-header-widget__description h5"
                content={page}
                attribute="belonging"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="page-header-widget__container --contact">
        <div className="container">
          <div className="row">
            <div className={mediaClass} />
            <div className={contentClass}>
              <div className="page-header-widget__area"></div>
            </div>
          </div>
        </div>
      </div>

      {children.length > 0 && (
        <div className="sticky-top">
          <InpageNavigationWidget
            widget={null}
            options={{
              largeTopMargin: true,
            }}
          />
        </div>
      )}

      <Scrivito.ContentTag
        tag="div"
        content={page}
        attribute="inpageNavigation"
        className="container s-py-3"
      />

      <div
        className="container s-py-3 contact_page"
        itemScope
        itemType="http://schema.org/Person"
      >
        <div className="contact-page__data row justify-content-md-center">
          <h4 className="mb-2 text-blue">
            {page.language() === "de" ? "Kontakt" : "Contact"}
          </h4>
          <div className="col-12 col-md-6">
            {contactItems.map((item, index) => (
              <Item key={index} {...item} />
            ))}

            <ReferenceItem
              text={I18n.t("ContactPage.items.Sekretariat")}
              content={page.get("sekretariat")}
            />
            <ul className="unstyled-list mt-4">
              {belongings.map((el, index) => (
                <li key={index} className="mt-1">
                  <Scrivito.LinkTag to={el} className="text-black">
                    <i className="las la-link me-1" />
                    {el.get("title")}
                  </Scrivito.LinkTag>
                </li>
              ))}
            </ul>
          </div>

          <div className="col-12 col-md-6">
            <ListWithTitle
              listItems={page.get("speciality")}
              title={I18n.t("ContactPage areas of expertise")}
            />

            <ListWithTitle
              listItems={page.get("faculty")}
              title={I18n.t("ContactPage faculties")}
            />
          </div>

          {hasContent && (
            <Scrivito.ContentTag
              tag="div"
              content={page}
              attribute="content"
              className="col-12"
              role="main"
            />
          )}
        </div>
      </div>

      <Scrivito.ContentTag tag="div" content={page} attribute="body" />
      <SchemaWidgetComponent type="Person" json={json} />
    </>
  );
}

/* Helper Components */

function ListWithTitle({ title, listItems }) {
  if (!listItems || listItems.length === 0) {
    return null;
  }

  return (
    <>
      <h4 className="mb-1 text-blue">{title}</h4>
      <ul className="mb-3">
        {listItems.map((s, index) => (
          <li key={index}>{s}</li>
        ))}
      </ul>
    </>
  );
}

const ReferenceItem = ({ text, content }) => {
  if (!content) {
    return null;
  }

  return (
    <div>
      <strong>{text}: </strong>
      <Scrivito.LinkTag to={content}>
        <span className="secretariat">{content.get("title")}</span>
      </Scrivito.LinkTag>
    </div>
  );
};

const contentTransform = (text, content) => {
  switch (text) {
    case "pronomen":
      return I18n.t(`ContactPage.content.${content.replace("/", "_")}`);
    case "Officeadress":
      return (
        <>
          <br />
          <span
            dangerouslySetInnerHTML={{
              __html: content.replace(",", "<br />"),
            }}
          />
        </>
      );
    case "Postadresse":
      return (
        <>
          <br />
          <span
            dangerouslySetInnerHTML={{
              __html: content.replace(",", "<br />"),
            }}
          />
        </>
      );
    default:
      return content;
  }
};

const ItemContent = ({ text, content, itemProp, title = true }) => (
  <div className={itemProp === "address" ? "mb-4" : "mb-1"}>
    {title && (
      <strong>{I18n.t(`ContactPage.items.${text}`, { ns: "live" })}: </strong>
    )}
    <span itemProp={itemProp}>{contentTransform(text, content)}</span>
  </div>
);

const Item = ({ text, content, href, itemProp }) => {
  if (!content) {
    return null;
  }

  return href ? (
    <a className="text-black" href={href}>
      <ItemContent
        text={text}
        content={content}
        itemProp={itemProp}
        title={false}
      />
    </a>
  ) : (
    <ItemContent text={text} content={content} itemProp={itemProp} />
  );
};

const PageWithLifetime = HidePageIfInactive(ContactPage);
Scrivito.provideComponent("ContactPage", PageWithLifetime);
